/**
 * StreamingChat Component
 * 
 * Provides a chat interface with real-time streaming response capabilities via WebSocket.
 * Displays responses incrementally as chunks arrive from the backend in real-time.
 * 
 * Requirements: 5.3, 10.1, 10.2, 10.3, 10.4
 */

import React, { useState, useRef } from 'react';
import { streamChatResponseWS } from '../services/websocketApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { flushSync } from 'react-dom';

interface StreamingChatProps {
  onSubmit?: (question: string) => void;
}

export const StreamingChat: React.FC<StreamingChatProps> = ({ onSubmit }) => {
  const [question, setQuestion] = useState<string>('');
  const [response, setResponse] = useState<string>('');
  const [jsonResponse, setJsonResponse] = useState<any>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [isJsonResponse, setIsJsonResponse] = useState<boolean>(false);
  
  // Use ref to track response for immediate updates
  const responseRef = useRef<string>('');

  /**
   * Handle form submission and initiate streaming
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate question
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      setValidationError('Please enter a question.');
      return;
    }

    // Clear previous state
    setValidationError('');
    setError(null);
    setResponse('');
    responseRef.current = '';
    setJsonResponse(null);
    setIsJsonResponse(false);
    setIsStreaming(true);

    // Notify parent component if callback provided
    if (onSubmit) {
      onSubmit(trimmedQuestion);
    }

    try {
      await streamChatResponseWS(trimmedQuestion, {
        onChunk: (text: string) => {
          // Update ref immediately
          responseRef.current += text;
          // Force synchronous state update for immediate DOM rendering
          flushSync(() => {
            setResponse(responseRef.current);
          });
        },
        onJsonChunk: (jsonChunk: string) => {
          // Mark as JSON response
          setIsJsonResponse(true);
          // Accumulate JSON chunks
          responseRef.current += jsonChunk;
          flushSync(() => {
            setResponse(responseRef.current);
          });
        },
        onJsonComplete: (json: any) => {
          // Store parsed JSON
          setJsonResponse(json);
        },
        onComplete: () => {
          setIsStreaming(false);
        },
        onError: (err: string) => {
          setError(err);
          setIsStreaming(false);
        },
        onOpen: () => {
          console.log('WebSocket connection established');
        },
        onClose: () => {
          console.log('WebSocket connection closed');
        }
      });
    } catch (err) {
      // Error already handled by onError callback
      // This catch is for any unexpected errors
      if (!error) {
        setError(err instanceof Error ? err.message : 'Request failed');
        setIsStreaming(false);
      }
    }
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  return (
    <div className="streaming-chat">
      {/* Question Input Form */}
      <form onSubmit={handleSubmit} className="streaming-chat-form">
        <div className="input-container">
          <textarea
            value={question}
            onChange={handleInputChange}
            placeholder="Ask a question about AI and machine learning..."
            disabled={isStreaming}
            className={`question-textarea ${validationError ? 'error' : ''}`}
            rows={4}
            aria-label="Question input"
            aria-invalid={!!validationError}
            aria-describedby={validationError ? 'validation-error' : undefined}
          />
          {validationError && (
            <div id="validation-error" className="validation-error" role="alert">
              {validationError}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={isStreaming}
          className="submit-button"
          aria-label="Submit question"
        >
          {isStreaming ? 'Streaming...' : 'Ask Question'}
        </button>
      </form>

      {/* Response Display */}
      <div className="streaming-response-container">
        {/* Loading Indicator - shown when streaming starts */}
        {isStreaming && !response && (
          <div className="streaming-loading" role="status" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>Getting your answer...</p>
          </div>
        )}

        {/* Streaming Response - shown when chunks arrive */}
        {response && (
          <div className="streaming-response" role="region" aria-label="Answer">
            <div className="streaming-response-header">
              <h3>Answer</h3>
              {isStreaming && (
                <div className="streaming-indicator" aria-live="polite">
                  <span className="streaming-dot"></span>
                  <span className="streaming-text">Streaming...</span>
                </div>
              )}
              {!isStreaming && !error && (
                <div className="completion-indicator" aria-live="polite">
                  <span className="completion-icon">✓</span>
                  <span className="completion-text">Complete</span>
                </div>
              )}
            </div>
            <div className="streaming-response-content">
              {isJsonResponse && jsonResponse ? (
                // Display structured JSON (e.g., quiz)
                <div className="json-response">
                  <pre>{JSON.stringify(jsonResponse, null, 2)}</pre>
                </div>
              ) : (
                // Display markdown text
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{response}</ReactMarkdown>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="streaming-error" role="alert" aria-live="assertive">
            <div className="error-icon" aria-hidden="true">⚠️</div>
            <p className="error-message">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isStreaming && !response && !error && (
          <div className="streaming-empty">
            <p className="empty-message">Ask a question to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
};
